import { useMemo } from 'react';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { AssistQuery } from 'services/assist-api/query';
import { useQueryAssist } from 'services/assist-api/query-client';

export type BaseLevel = API_JSON & {
  name: string;
  value: string;
  order: number;
  enabled: boolean;
};

const order = { 'order[order]': 'asc' } as const;
const groups = [];
/**
 * Fetch levels
 * @param params can be used to customise/limit the levels retrieved
 * @returns an array of levels
 */
export const getLevels: GetMany<BaseLevel> = async (params) =>
  (await ASSIST_API.get('levels', { params: requestParams({ order, groups }, params) })).data;

/**
 * Fetch level (singular)
 * @param params can be used to customise/limit the levels retrieved
 * @returns a single level
 */
export const getLevel: GetOne<BaseLevel> = async (id, params) =>
  (await ASSIST_API.get(`levels/${id}`, { params: requestParams({ order, groups }, params) })).data;

/**
 * Hook implementation to fetch levels
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns levels matching request
 */
export const useLevels: AssistQuery.UseMany<BaseLevel> = (params, options) =>
  useQueryAssist(['levels', params], () => getLevels(params), {
    ...options,
    staleTime: STALE_LONG,
    allowNoFilters: true
  });

/**
 * Given an levelId fetch and return the level
 *  - only runs once levelId is provided
 *  - fetches the one level
 *  - caches data
 * @param id levelId
 * @param params optional parameters to change requested data
 * @returns the matching level
 */
export const useLevel: AssistQuery.UseOne<BaseLevel> = (id, params, options) =>
  useQueryAssist(['levels', { id, ...params }], () => getLevel(id, params), {
    staleTime: STALE_LONG,
    ...options,
    enabled: id !== null && id !== undefined // id: 0 is a valid id on this entity, so we need to specifically check against null and undefined
  });

/**
 * Given an levelId fetch and return the level
 *  - fetches all levels (relatively cheap)
 *  - caches data
 * @param id levelId
 * @returns the matching level
 */
export const useLevelById = (id: number): BaseLevel | undefined => {
  const { data } = useLevels();
  return useMemo(() => data?.json.find((level) => level.id === id), [data, id]);
};

/**
 * Given an levelId fetch and return the level
 *  - fetches all levels (relatively cheap)
 *  - caches data
 * @param levelIds
 * @returns the matching level
 */
export const useLevelsByIds = (levelIds: number[]): BaseLevel[] | undefined => {
  const { data } = useLevels();
  return useMemo(
    () => data?.json.filter((level) => levelIds.find((id) => level.id === id)),
    [data, levelIds]
  );
};

export const useEnabledLevels = (): BaseLevel[] | undefined => {
  const { data } = useLevels();
  return useMemo(() => data?.json.filter((level) => level.enabled), [data]);
};
